import {FormikProps} from 'formik';
import {filter, isInteger, map} from 'lodash';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ActionButton, ConfirmModal} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {SpecialDayRepeat, SpecialDayWorkingHoursItem, WorkingHours} from 'types';

import {FIELDS} from '../constants';

import {SpecialHoursItem, SpecialHoursItemMode} from './SpecialHoursItem';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.WORKING_HOURS;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type Props = FormikProps<WorkingHours>;

export const SpecialHours: React.FC<Props> = (props) => {
    const {t} = useTranslation();
    const [addSpecialHoursFormVisible, setAddSpecialHoursFormVisible] = useState(false);
    const [confirmRemoveModalProps, setConfirmRemoveModalProps] = useState<{
        open: boolean,
        index?: number
    }>({open: false});

    const openConfirmRemoveModal = useCallback((index: number) => {
        const item = props.values.specialHours[index];
        if (item) {
            setConfirmRemoveModalProps({open: true, index});
        }
    }, [props.values]);

    const defaultSpecialHour: SpecialDayWorkingHoursItem = useMemo(() => {
        const now = new Date();
        return {
            day: now.getDate(),
            month: now.getMonth(),
            year: now.getFullYear(),
            repeat: SpecialDayRepeat.NoRepeat,
            intervals: []
        };
    }, []);

    const handleRemove = useCallback((confirm: boolean) => {
        if (confirm && isInteger(confirmRemoveModalProps.index)) {
            const updatedSpecialHours = filter(props.values.specialHours, (_, index) => index !== confirmRemoveModalProps.index);
            props.setFieldValue(FIELDS.SPECIAL_HOURS, updatedSpecialHours);
        }
        setConfirmRemoveModalProps({open: false});
    }, [confirmRemoveModalProps.index, props]);

    return (
        <div className='-2xl:tw-mt-8 2xl:tw-ml-4 2xl:tw-w-1/2'>
            <div className='tw-ts-h5-bold'>{t(T_KEYS.SPECIAL_HOURS.TITLE)}</div>
            <div className='sm:tw-flex tw-items-center tw-justify-between tw-my-4'>
                <div className='-sm:tw-mb-2'>{t(T_KEYS.SPECIAL_HOURS.DESCRIPTION)}</div>
                <ActionButton
                    disabled={addSpecialHoursFormVisible}
                    action='create'
                    className='2xl:tw-ml-6 -2xl:tw-mt-2'
                    onClick={() => setAddSpecialHoursFormVisible(true)}
                >
                    {t(T_KEYS_COMMON.BUTTONS.CREATE)}
                </ActionButton>
            </div>
            {addSpecialHoursFormVisible &&
                <SpecialHoursItem
                    {...props}
                    mode={SpecialHoursItemMode.Create}
                    defaultValue={defaultSpecialHour}
                    onClose={() => setAddSpecialHoursFormVisible(false)}
                />}
            {map(props.values.specialHours, (_, index) => (
                <SpecialHoursItem
                    key={index}
                    {...props}
                    index={index}
                    mode={SpecialHoursItemMode.Update}
                    onRemove={() => openConfirmRemoveModal(index)}
                />
            ))}
            <ConfirmModal {...confirmRemoveModalProps} onClose={handleRemove}>
                <div>{t(T_KEYS.SPECIAL_HOURS.CONFIRM_REMOVE_MODAL.LABEL)}</div>
            </ConfirmModal>
        </div>
    );
};